import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { PARYUSHANA_DATES } from '@/lib/constants';
import ProfileCard from '@/components/ProfileCard';
import MatchAnimation from '@/components/MatchAnimation';
import { X, Star, Heart, Lock, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import '@/App.css';

function isParyushana(sect) {
  const now = new Date();
  const dates = PARYUSHANA_DATES[sect] || PARYUSHANA_DATES.Shvetambara;
  if (!dates) return false;
  return now >= new Date(dates.start+'T00:00:00') && now <= new Date(dates.end+'T23:59:59');
}

export default function Feed() {
  const { user, profile, refreshProfile } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ageFilter, setAgeFilter] = useState(null);
  const [actionsToday, setActionsToday] = useState(0);
  const [showJJConfirm, setShowJJConfirm] = useState(false);
  const [showMatch, setShowMatch] = useState(null);
  const [showScreenshotWarn, setShowScreenshotWarn] = useState(false);
  const [jjUsed, setJjUsed] = useState(true);
  const [tapAnim, setTapAnim] = useState(null);
  const tapTimeout = useRef(null);

  // Daily action limit
  const DAILY_LIMIT = 7;
  const remainingToday = Math.max(0, DAILY_LIMIT - actionsToday);
  const hasReachedDailyLimit = actionsToday >= DAILY_LIMIT;

  const sessionCount = parseInt(localStorage.getItem('jiv_session_count') || '0');
  const showActionLabel = sessionCount < 3;
  const jjFirstUsed = localStorage.getItem('jiv_jj_first_use') === 'true';

  useEffect(() => {
    // Increment session count
    const c = parseInt(localStorage.getItem('jiv_session_count') || '0');
    localStorage.setItem('jiv_session_count', String(c + 1));
    // Check notifications
    if (user?.id) {
      const notifs = db.getNotifications(user.id);
      notifs.forEach(n => toast(n.message));
      if (notifs.length > 0) db.markNotificationsRead(user.id);
    }
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    if (!user?.id) {
      setActionsToday(0);
      return undefined;
    }

    db.getDailyActionCount(user.id).then(count => {
      if (mounted) setActionsToday(count);
    });

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) db.canUseJaiJinendra(user.id).then(canUse => setJjUsed(!canUse));
  }, [user?.id]);

  const loadProfiles = useCallback(async () => {
    if (user && profile && !profile.profile_paused) {
      await db.syncDiscoveryProfiles(user.id, profile.seeking, profile.city);
      setProfiles(db.getDiscoveryProfiles(user.id, profile.seeking, profile.city, ageFilter));
    }
  }, [user, profile, ageFilter]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  // Screenshot detection
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'hidden') {
        const c = parseInt(localStorage.getItem('jiv_screenshot_count') || '0') + 1;
        localStorage.setItem('jiv_screenshot_count', String(c));
        if (c === 1) toast("Screenshots aren't allowed on Jiv. This keeps everyone's privacy safe.");
        else if (c === 2) setShowScreenshotWarn(true);
        else if (c === 3 && user?.id) db.updateProfile(user.id, { screenshot_warned: true });
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [user?.id]);

  // Paryushana mode
  if (profile && isParyushana(profile.sect)) {
    const matches = db.getMatches(user.id);
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center bg-background" data-testid="paryushana-mode">
        <p className="text-2xl font-semibold text-foreground leading-tight">Jiv is taking a mindful pause for Paryushana.</p>
        <p className="text-[#7A7672] mt-2 text-sm">Your matches are still here.</p>
        <div className="mt-6 w-full max-w-sm">
          {matches.map(m => (
            <div key={m.id} className="flex items-center gap-3 p-3 border-b border-border">
              <div className="w-10 h-10 rounded-full bg-muted overflow-hidden shrink-0">
                {m.otherPhotos?.[0] && <img src={m.otherPhotos[0].url} className="w-full h-full object-cover no-screenshot" alt="" onContextMenu={(e) => e.preventDefault()} draggable={false} />}
              </div>
              <span className="text-foreground text-sm">{m.otherProfile?.display_name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (profile?.profile_paused) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center" data-testid="feed-paused">
        <p className="text-lg font-medium text-[#1A1814]">Your profile is paused</p>
        <p className="text-sm text-[#7A7672] mt-2">You won't appear in anyone's feed while paused.</p>
        <button
          onClick={async () => {
            await supabase.from('profiles').update({ profile_paused: false }).eq('id', user.id);
            if (refreshProfile) refreshProfile();
          }}
          className="mt-5 px-6 py-2.5 rounded-full bg-[#C857A8] text-white text-sm font-semibold"
        >
          Resume
        </button>
      </div>
    );
  }

  const currentProfile = profiles[currentIndex];

  const animateButton = (type) => {
    setTapAnim(type);
    if (tapTimeout.current) clearTimeout(tapTimeout.current);
    tapTimeout.current = setTimeout(() => setTapAnim(null), 200);
  };

  const handleAction = async (type, promptIndex) => {
    if (!currentProfile) return;

    if (hasReachedDailyLimit) {
      toast('You\'ve reached your daily limit of 7 interactions. Come back tomorrow!');
      return;
    }

    animateButton(type);

    if (type === 'jai_jinendra') {
      if (jjUsed) { toast('Your Jai Jinendra resets on Monday. Use it for someone who really stands out.'); return; }
      if (!jjFirstUsed) { setShowJJConfirm(true); return; }
      await db.useJaiJinendra(user.id);
      setJjUsed(true);
    }

    setActionsToday(prev => Math.min(DAILY_LIMIT, prev + 1));

    const result = await db.createInteraction(user.id, currentProfile.id, type, promptIndex || null);

    if (result.isMatch) setShowMatch(currentProfile);
    else setCurrentIndex(prev => prev + 1);
  };

  const confirmJJ = async () => {
    localStorage.setItem('jiv_jj_first_use', 'true');
    setShowJJConfirm(false);
    setActionsToday(prev => Math.min(DAILY_LIMIT, prev + 1));
    await db.useJaiJinendra(user.id);
    setJjUsed(true);
    const result = await db.createInteraction(user.id, currentProfile.id, 'jai_jinendra', null);
    if (result.isMatch) setShowMatch(currentProfile);
    else setCurrentIndex(prev => prev + 1);
  };

  const isDailyLimitReached = actionsToday >= DAILY_LIMIT;

  if (!currentProfile || isDailyLimitReached) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center" data-testid="feed-empty">
        <svg viewBox="0 0 100 100" width="80" height="80" className="mb-6">
          <circle cx="38" cy="50" r="30" fill="#C857A8" opacity="0.25" />
          <circle cx="62" cy="50" r="30" fill="#C857A8" opacity="0.25" />
          <path d="M50 35 L53 47 L65 50 L53 53 L50 65 L47 53 L35 50 L47 47 Z" fill="#C857A8" opacity="0.6" />
        </svg>
        {isDailyLimitReached ? (
          <>
            <p className="text-lg font-medium text-[#1A1814]">You've hit your daily limit</p>
            <p className="text-sm text-[#7A7672] mt-2">You've had 7 meaningful interactions today. Come back tomorrow — your feed refreshes at midnight.</p>
          </>
        ) : (
          <>
            <p className="text-lg font-medium text-[#1A1814]">You've seen everyone nearby</p>
            <p className="text-sm text-[#7A7672] mt-2">New members join every day. Come back tomorrow.</p>
          </>
        )}
        <Sheet>
          <SheetTrigger asChild>
            <button className="text-[13px] text-[#C857A8] font-medium mt-3" data-testid="adjust-filters-btn">Adjust filters</button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-[20px]">
            <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
            <div className="py-4 space-y-5">
              <div>
                <p className="text-sm font-medium mb-2">Age range</p>
                <Slider value={ageFilter || [18, 55]} onValueChange={v => { setAgeFilter(v); setCurrentIndex(0); }} min={18} max={55} step={1} />
                <p className="text-xs text-[#7A7672] mt-1">{ageFilter ? `${ageFilter[0]} - ${ageFilter[1]} years` : 'Any age'}</p>
              </div>
              {ageFilter && <button onClick={() => { setAgeFilter(null); setCurrentIndex(0); loadProfiles(); }} className="text-xs text-[#C857A8] font-medium">Clear all filters</button>}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative" data-testid="discovery-feed">
      {/* Header with daily limit counter */}
      <div className="px-4 pt-3 pb-2 shrink-0 flex items-center justify-between bg-white border-b border-[rgba(0,0,0,0.05)]">
        <h2 className="text-sm font-medium text-[#1A1814]">Discovery</h2>
        <span className="text-xs font-medium text-[#A8A5A0]">{remainingToday} of 7 left today</span>
      </div>

      {/* Filter button */}
      <div className="absolute top-14 right-3 z-30">
        <Sheet>
          <SheetTrigger asChild>
            <button className="w-9 h-9 rounded-full bg-white/80 backdrop-blur flex items-center justify-center" data-testid="filter-btn">
              <SlidersHorizontal className="h-4 w-4 text-[#1A1814]" />
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-[20px]">
            <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
            <div className="py-4 space-y-5">
              <div>
                <p className="text-sm font-medium mb-2">Age range</p>
                <Slider value={ageFilter || [18, 55]} onValueChange={v => { setAgeFilter(v); setCurrentIndex(0); }} min={18} max={55} step={1} />
                <p className="text-xs text-[#7A7672] mt-1">{ageFilter ? `${ageFilter[0]} - ${ageFilter[1]} years` : 'Any age'}</p>
              </div>
              {ageFilter && <button onClick={() => { setAgeFilter(null); setCurrentIndex(0); }} className="text-xs text-[#C857A8] font-medium">Clear all filters</button>}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Card scroll area */}
      <div className="flex-1 overflow-auto hide-scrollbar px-3 pt-1 pb-28">

        {/* Regular feed card */}
        {currentProfile && (
          <div className={currentProfile.hasJaiJinendra ? 'ring-2 ring-[#C857A8] ring-offset-2 rounded-[20px]' : ''}>
            <ProfileCard profile={currentProfile} onPromptLike={(idx) => handleAction('like', idx)} />
          </div>
        )}
      </div>

      {/* Floating action bar */}
      <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center z-30" data-testid="action-bar">
        <div className="bg-white rounded-[40px] px-7 py-2.5 flex items-center gap-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
          {/* Pass */}
          <button
            onClick={() => handleAction('pass')}
            className={`w-11 h-11 rounded-full border-2 border-[#D3D0CB] flex items-center justify-center ${tapAnim === 'pass' ? 'animate-tap-pop' : ''}`}
            data-testid="pass-button"
          >
            <X className="h-5 w-5 text-[#888780]" />
          </button>

          {/* Jai Jinendra */}
          <button
            onClick={() => handleAction('jai_jinendra')}
            className={`w-[52px] h-[52px] rounded-full bg-[#C857A8] flex items-center justify-center relative ${tapAnim === 'jai_jinendra' ? 'animate-tap-pop' : ''} ${!jjUsed && jjFirstUsed ? 'animate-pulse-star' : ''}`}
            data-testid="jai-jinendra-button"
          >
            {jjUsed ? <Lock className="h-5 w-5 text-white/60" /> : <Star className="h-5 w-5 text-white fill-white" />}
          </button>

          {/* Like */}
          <button
            onClick={() => handleAction('like')}
            className={`w-11 h-11 rounded-full border-2 border-[#C857A8] flex items-center justify-center ${tapAnim === 'like' ? 'animate-tap-pop' : ''}`}
            data-testid="like-button"
          >
            <Heart className="h-5 w-5 text-[#C857A8]" />
          </button>
        </div>

        {/* First 3 sessions label */}
        {showActionLabel && (
          <p className="text-[11px] text-[#A8A5A0] mt-2 text-center">Heart = like quietly · Star = Jai Jinendra, once a week</p>
        )}
      </div>

      {/* Match animation */}
      {showMatch && (
        <MatchAnimation
          profile={showMatch}
          onMessage={() => {
            const matches = db.getMatches(user.id);
            const m = matches.find(mm => mm.otherProfile?.id === showMatch.id);
            setShowMatch(null); setCurrentIndex(prev => prev + 1);
            if (m) window.location.href = `/messages/${m.id}`;
          }}
          onContinue={() => { setShowMatch(null); setCurrentIndex(prev => prev + 1); }}
        />
      )}

      {/* Jai Jinendra first-use confirm */}
      <Dialog open={showJJConfirm} onOpenChange={setShowJJConfirm}>
        <DialogContent className="max-w-xs rounded-[20px]">
          <DialogHeader>
            <DialogTitle className="text-lg">Jai Jinendra</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              The traditional Jain greeting. You get one per week. They'll know you genuinely mean it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="ghost" onClick={() => setShowJJConfirm(false)} className="rounded-full flex-1">Not yet</Button>
            <Button onClick={confirmJJ} className="rounded-full flex-1 bg-[#C857A8] hover:bg-saffron-mid" data-testid="confirm-jj-btn">Send it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screenshot warning modal */}
      <Dialog open={showScreenshotWarn} onOpenChange={setShowScreenshotWarn}>
        <DialogContent className="max-w-xs rounded-[20px]">
          <DialogHeader>
            <DialogTitle>Second warning</DialogTitle>
            <DialogDescription>Repeated screenshots may result in your account being reviewed by our team.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowScreenshotWarn(false)} className="rounded-full w-full">Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
